import React, { useState, useEffect, useCallback } from "react";
import { Pagination, Stack } from "@mui/material";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import StudentSearchSelect from "../components/common/StudentSearchSelect";
import DateRangeFilter from "../components/common/DateRangeFilter";
import PaymentCard from "../components/payments/PaymentCard";

import useDebounce from "../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getPayments } from "../redux/actions/paymentActions";
import { PAYMENT_DIRECTION_OPTIONS } from "../shared/data/enumOptions";
import { PAYMENT_METHOD_OPTIONS } from "../shared/data/enumOptions";
import { PaymentDirection } from "../shared/enums/PaymentDirection";
import { PaymentMethod } from "../shared/enums/PaymentMethod";

const PaymentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const today = new Date().toISOString().split("T")[0];

  const {
    items: payments,
    pagination,
    loading,
    error,
  } = useAppSelector((state) => state.payment);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [selectedDirection, setSelectedDirection] = useState<string>("");
  const [selectedMethod, setSelectedMethod] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const limit = 12;

  const fetchPaymentsList = useCallback((): void => {
    dispatch(
      getPayments({
        page,
        size: limit,
        search: debouncedSearch.trim() || undefined,
        startDate: startDate ? `${startDate}` : undefined,
        endDate: endDate ? `${endDate}` : undefined,
        branchId: selectedBranchId || undefined,
        studentId: selectedStudentId || undefined,
        direction: (selectedDirection as PaymentDirection) || undefined,
        paymentMethod: (selectedMethod as PaymentMethod) || undefined,
      }),
    );
  }, [
    dispatch,
    page,
    limit,
    debouncedSearch,
    startDate,
    endDate,
    selectedBranchId,
    selectedStudentId,
    selectedDirection,
    selectedMethod,
  ]);

  useEffect(() => {
    fetchPaymentsList();
  }, [fetchPaymentsList]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    startDate,
    endDate,
    selectedBranchId,
    selectedDirection,
    selectedMethod,
  ]);

  return (
    <PageContainer
      title="Payments History"
      subtitle="View all incoming and outgoing payments."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by reference, narrative..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={setSelectedBranchId}
        />

        <StudentSearchSelect
          value={selectedStudentId}
          branchId={selectedBranchId}
          onChange={(id) => {
            setSelectedStudentId(id);
            setPage(1);
          }}
        />

        <AppSelectFilter
          label="Direction"
          value={selectedDirection}
          onChange={setSelectedDirection}
          options={PAYMENT_DIRECTION_OPTIONS}
          allLabel="All Directions"
          minWidth={140}
        />

        <AppSelectFilter
          label="Payment Method"
          value={selectedMethod}
          onChange={setSelectedMethod}
          options={PAYMENT_METHOD_OPTIONS}
          allLabel="All Methods"
          minWidth={150}
        />

        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loading}
        error={error}
        itemCount={payments?.length || 0}
        emptyMessage="No payments found"
        onRetry={fetchPaymentsList}
      >
        {payments?.map((payment) => (
          <PaymentCard key={payment.id} payment={payment} />
        ))}
      </CardGridContainer>

      {pagination?.totalPages && pagination.totalPages > 1 && (
        <Stack sx={{ alignItems: "center", mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(_, val) => setPage(val)}
            color="primary"
          />
        </Stack>
      )}
    </PageContainer>
  );
};

export default PaymentsPage;
